import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLingui } from '@lingui/react/macro'

import Dropdown from '@/components/ui/Dropdown'
import Field from '@/components/ui/Field'
import { useOrganization } from '@/features/organization'
import { QrCodeContent } from '../hooks/useQrCode'
import { SpaceUrl } from './SpaceUrl'
import { GoodieUrl } from './GoodieUrl'
import { DirectLoginUrl } from './DirectLoginUrl'

enum QR_TYPES {
  FEED = 'feed',
  GOODIES = 'goodies',
  GUEST_PROFILE = 'guest_profile',
  GOODIE = 'goodie',
  LOGIN = 'login',
  SPACE_LOGIN = 'space-login',
  DIRECT_LOGIN = 'direct_login'
}

function useTypeOptions() {
  const { t } = useLingui()

  return useMemo(() => {
    return [
      {
        label: 'Feed',
        value: QR_TYPES.FEED
      },
      {
        label: t`Goodies Pages`,
        value: QR_TYPES.GOODIES
      },
      {
        label: t`Guest area`,
        value: QR_TYPES.GUEST_PROFILE
      },
      {
        label: 'Goodie',
        value: QR_TYPES.GOODIE
      },
      {
        label: t`Login Page`,
        value: QR_TYPES.LOGIN
      },
      {
        label: t`Login with Room`,
        value: QR_TYPES.SPACE_LOGIN
      },
      {
        label: t`Direct Login`,
        value: QR_TYPES.DIRECT_LOGIN
      }
    ]
  }, [t])
}

export function SimpleUrl({
  path,
  appUrl,
  onChange
}: {
  path: string
  appUrl: string
  onChange: (urls: QrCodeContent[]) => void
}) {
  useEffect(() => {
    onChange([{ url: `${appUrl}${path}` }])
  }, [path, appUrl, onChange])

  return null
}

export function TypeForm({
  onChange
}: {
  onChange: (urls: QrCodeContent[]) => void
}) {
  const { t } = useLingui()
  const typeOptions = useTypeOptions()
  const { appUrl } = useOrganization()

  const [type, setType] = useState<QR_TYPES>(QR_TYPES.FEED)

  const onChangeType = useCallback(
    (newType: QR_TYPES) => {
      if (type !== newType) onChange([])

      setType(newType)
    },
    [type, setType, onChange]
  )

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-heading3 text-text">Ziel</h2>
      <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
        <div>
          <Field label={t`Type`} size="small">
            <Dropdown
              options={typeOptions}
              value={type}
              onChange={onChangeType}
            />
          </Field>
        </div>
        {type === QR_TYPES.FEED && (
          <SimpleUrl appUrl={appUrl} onChange={onChange} path="/" />
        )}
        {type === QR_TYPES.LOGIN && (
          <SimpleUrl appUrl={appUrl} onChange={onChange} path="/connect" />
        )}
        {type === QR_TYPES.GOODIES && (
          <SimpleUrl appUrl={appUrl} onChange={onChange} path="/goodies" />
        )}
        {type === QR_TYPES.GUEST_PROFILE && (
          <SimpleUrl appUrl={appUrl} onChange={onChange} path="/profile" />
        )}
        {type === QR_TYPES.SPACE_LOGIN && (
          <SpaceUrl appUrl={appUrl} onChange={onChange} />
        )}
        {type === QR_TYPES.GOODIE && (
          <GoodieUrl appUrl={appUrl} onChange={onChange} />
        )}
        {type === QR_TYPES.DIRECT_LOGIN && (
          <DirectLoginUrl appUrl={appUrl} onChange={onChange} />
        )}
      </div>
    </div>
  )
}
